import { GstQuestionCard } from '../modules/settings/gst-question-card'
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  KpiCard,
  Skeleton,
  cn,
  formatDateTime,
  formatMoney,
  greeting,
} from '@bixo/ui'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import {
  AlertTriangle,
  ArrowRight,
  BarChart3,
  CircleDollarSign,
  ClipboardList,
  HandCoins,
  Landmark,
  PackageX,
  Percent,
  Plus,
  ShieldCheck,
  Store,
  Timer,
  TrendingUp,
  UserPlus,
  Wallet,
  type LucideIcon,
} from 'lucide-react'
import { motion } from 'motion/react'
import type { ReactNode } from 'react'
import { tenantQuery } from '../lib/api'
import { can, useMe } from '../lib/auth'
import { dashboardApi, rk } from '../modules/reports/api'
import { OrdinalDonut, RankedBars, TrendLines } from '../modules/reports/charts'
import { AGEING_LABELS, type AgeingBucket, type Dashboard } from '../modules/reports/types'

const pct = (v: string | null | undefined): number | undefined =>
  v === null || v === undefined ? undefined : Number(v)

export function DashboardPage() {
  const me = useMe()
  const { data: tenant } = useQuery(tenantQuery)
  const { data, isLoading } = useQuery({
    queryKey: rk.dashboard(),
    queryFn: () => dashboardApi.get(),
    enabled: can(me, 'dashboard.view'),
    refetchInterval: 60_000,
  })
  const firstName = me.name.split(' ')[0] ?? me.name
  const today = new Intl.DateTimeFormat('en-IN', { weekday: 'long', day: 'numeric', month: 'long' }).format(
    new Date(),
  )
  const k = data?.kpis

  return (
    <div className="space-y-6">
      <motion.section
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="bg-brand-mesh relative overflow-hidden rounded-2xl p-6 text-white shadow-elevated sm:p-8"
      >
        <div className="bg-grid absolute inset-0 opacity-25 mix-blend-overlay" aria-hidden />
        <div
          className="absolute -top-24 -right-16 size-72 rounded-full bg-brand-300/25 blur-3xl"
          aria-hidden
        />
        <div className="relative flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-sm text-white/70">{today}</p>
            <h1 className="mt-1 text-2xl font-semibold tracking-tight sm:text-3xl">
              {greeting()}, {firstName}
            </h1>
            <p className="mt-2 max-w-xl text-sm text-white/75">
              {k?.sales
                ? `${formatMoney(k.sales.today)} sold today and ${formatMoney(k.sales.mtd)} this month${data?.scope === 'own' ? ' (your own sales)' : ''}.`
                : `Welcome to ${tenant?.app_name ?? 'your workspace'}.`}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {can(me, 'sales.create') && (
              <Button asChild className="bg-white text-brand-800 hover:bg-white/90">
                <Link to="/sales/invoices/new">
                  <Plus /> New invoice
                </Link>
              </Button>
            )}
            {can(me, 'pos.create') && (
              <Button
                asChild
                className="bg-white/10 text-white ring-1 ring-white/25 backdrop-blur hover:bg-white/20"
              >
                <Link to="/pos">
                  <Store /> Open POS
                </Link>
              </Button>
            )}
            {can(me, 'reports.view') && (
              <Button
                asChild
                className="bg-white/10 text-white ring-1 ring-white/25 backdrop-blur hover:bg-white/20"
              >
                <Link to="/reports">
                  <BarChart3 /> Reports
                </Link>
              </Button>
            )}
            {!can(me, 'sales.create') && can(me, 'users.create') && (
              <Button
                asChild
                className="bg-white/10 text-white ring-1 ring-white/25 backdrop-blur hover:bg-white/20"
              >
                <Link to="/settings/users">
                  <UserPlus /> Invite teammate
                </Link>
              </Button>
            )}
          </div>
        </div>
      </motion.section>

      {data?.shift && <ShiftCard shift={data.shift} />}

      <Pending pending={data?.pending} />

      {(isLoading || (k && Object.keys(k).length > 0)) && (
        <section aria-label="Key figures" className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {(isLoading || k?.sales) && (
            <KpiCard
              index={0}
              loading={isLoading}
              label="Sales this month"
              value={formatMoney(k?.sales?.mtd)}
              icon={CircleDollarSign}
              trend={pct(k?.sales?.change_pct)}
              hint={k?.sales ? `vs same days last month · today ${formatMoney(k.sales.today)}` : undefined}
            />
          )}
          {(isLoading || k?.collections) && (
            <KpiCard
              index={1}
              loading={isLoading}
              label="Collected this month"
              value={formatMoney(k?.collections?.mtd)}
              icon={Wallet}
              trend={pct(k?.collections?.change_pct)}
              hint={k?.collections ? `today ${formatMoney(k.collections.today)}` : undefined}
            />
          )}
          {(isLoading || k?.outstanding) && (
            <KpiCard
              index={2}
              loading={isLoading}
              label="Outstanding"
              value={formatMoney(k?.outstanding?.total)}
              icon={HandCoins}
              hint={
                k?.outstanding ? (
                  <span className={cn(Number(k.outstanding.overdue) > 0 && 'text-danger')}>
                    {formatMoney(k.outstanding.overdue)} overdue
                  </span>
                ) : undefined
              }
            />
          )}
          {k?.gross_margin ? (
            <KpiCard
              index={3}
              label="Gross margin this month"
              value={formatMoney(k.gross_margin.mtd)}
              icon={Percent}
              hint={
                k.gross_margin.mtd_pct !== null
                  ? `${Number(k.gross_margin.mtd_pct).toFixed(1)}% of sales`
                  : 'No sales yet'
              }
            />
          ) : k?.cash_bank ? (
            <KpiCard
              index={3}
              label="Cash & bank"
              value={formatMoney(k.cash_bank.total)}
              icon={Landmark}
              hint={`${k.cash_bank.accounts.length} accounts`}
            />
          ) : null}
        </section>
      )}

      {data?.trend && (
        <Card>
          <CardHeader className="flex-row items-start justify-between gap-4">
            <div>
              <CardTitle>Sales, last 30 days</CardTitle>
              <CardDescription>Daily sales before GST, against the 30 days before.</CardDescription>
            </div>
            {can(me, 'reports.view') && (
              <Link
                to={'/reports/sales_summary?group_by=day&preset=last_30_days' as never}
                className="text-sm font-medium text-primary hover:underline"
              >
                Details
              </Link>
            )}
          </CardHeader>
          <CardContent>
            <TrendLines
              data={data.trend.current.map((p, i) => ({
                label: p.label,
                current: p.value,
                previous: data.trend?.previous[i]?.value ?? '0',
              }))}
              x="label"
              series={[
                { key: 'current', label: 'Last 30 days' },
                { key: 'previous', label: 'Previous 30 days', muted: true, dashed: true },
              ]}
              height={260}
            />
          </CardContent>
        </Card>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        {data?.ageing && <AgeingCard ageing={data.ageing} />}
        {data?.top_items && (
          <ListCard title="Top items this month" empty="No sales yet this month" icon={TrendingUp}>
            {data.top_items.length > 0 ? (
              <RankedBars data={data.top_items} label="name" series={[{ key: 'value', label: 'Sales' }]} />
            ) : null}
          </ListCard>
        )}
        {data?.top_customers && (
          <ListCard title="Top customers this month" empty="No sales yet this month" icon={HandCoins}>
            {data.top_customers.length > 0 ? (
              <RankedBars
                data={data.top_customers}
                label="name"
                series={[{ key: 'value', label: 'Sales' }]}
              />
            ) : null}
          </ListCard>
        )}
        {data?.partner_leaderboard && (
          <Leaderboard title="Partner leaderboard" rows={data.partner_leaderboard} hrefBase="/partners/" />
        )}
        {data?.executive_leaderboard && (
          <Leaderboard title="Sales team this month" rows={data.executive_leaderboard} />
        )}
        {k?.cash_bank && <CashBank cash={k.cash_bank} />}
        {data?.low_stock && <LowStock rows={data.low_stock} />}
      </div>

      {isLoading && (
        <div className="grid gap-6 lg:grid-cols-2">
          <Skeleton className="h-72 rounded-2xl" />
          <Skeleton className="h-72 rounded-2xl" />
        </div>
      )}

      {can(me, 'settings.edit') && <GstQuestionCard />}

      {!me.two_factor_enabled && (
        <Card className="flex flex-col items-start gap-3 p-5 sm:flex-row sm:items-center">
          <ShieldCheck className="size-5 text-primary" />
          <div className="flex-1">
            <p className="text-sm font-medium">Protect your account</p>
            <p className="text-xs text-muted-foreground">
              Turn on two-factor sign-in with an authenticator app.
            </p>
          </div>
          <Button asChild size="sm" variant="secondary">
            <Link to="/profile" search={{ tab: 'security' }}>
              Set up
            </Link>
          </Button>
        </Card>
      )}
    </div>
  )
}

function ListCard({
  title,
  empty,
  icon: Icon,
  children,
}: {
  title: string
  empty: string
  icon: LucideIcon
  children: ReactNode
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {children ?? (
          <div className="flex flex-col items-center gap-2 py-10 text-sm text-muted-foreground">
            <Icon className="size-5" />
            {empty}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function AgeingCard({ ageing }: { ageing: Record<AgeingBucket, string> }) {
  const total = Object.values(ageing).reduce((s, v) => s + Number(v), 0)
  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-4">
        <div>
          <CardTitle>Receivables by age</CardTitle>
          <CardDescription>Open invoice balances, aged from the due date.</CardDescription>
        </div>
        <Link to="/receivables" className="text-sm font-medium text-primary hover:underline">
          Chase
        </Link>
      </CardHeader>
      <CardContent>
        <OrdinalDonut
          data={(Object.keys(AGEING_LABELS) as AgeingBucket[]).map((key) => ({
            key,
            label: AGEING_LABELS[key],
            value: Number(ageing[key]),
          }))}
          center={
            <div>
              <div className="tabular text-sm font-semibold">{formatMoney(total.toFixed(2))}</div>
              <div className="text-[11px] text-muted-foreground">outstanding</div>
            </div>
          }
        />
      </CardContent>
    </Card>
  )
}

function Leaderboard({
  title,
  rows,
  hrefBase,
}: {
  title: string
  rows: NonNullable<Dashboard['executive_leaderboard']>
  hrefBase?: string
}) {
  const max = Math.max(...rows.map((r) => Number(r.sales)), 1)
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">No sales yet this month</p>
        ) : (
          <ol className="grid gap-3">
            {rows.map((r, i) => (
              <li key={r.id} className="grid grid-cols-[1.5rem_1fr_auto] items-center gap-3 text-sm">
                <span
                  className={cn(
                    'grid size-6 place-items-center rounded-full text-xs font-semibold',
                    i === 0 ? 'bg-accent text-accent-foreground' : 'bg-muted text-muted-foreground',
                  )}
                >
                  {i + 1}
                </span>
                <div className="min-w-0">
                  {hrefBase ? (
                    <Link to={`${hrefBase}${r.id}` as never} className="truncate font-medium hover:underline">
                      {r.name}
                    </Link>
                  ) : (
                    <span className="truncate font-medium">{r.name}</span>
                  )}
                  <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full rounded-full bg-primary"
                      style={{ width: `${(Number(r.sales) / max) * 100}%` }}
                    />
                  </div>
                </div>
                <span className="tabular text-right">
                  {formatMoney(r.sales)}
                  <span className="block text-[11px] text-muted-foreground">{r.invoices} inv</span>
                </span>
              </li>
            ))}
          </ol>
        )}
      </CardContent>
    </Card>
  )
}

function CashBank({ cash }: { cash: NonNullable<Dashboard['kpis']['cash_bank']> }) {
  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-4">
        <div>
          <CardTitle>Cash & bank</CardTitle>
          <CardDescription className="tabular">{formatMoney(cash.total)} across all accounts</CardDescription>
        </div>
        <Link to="/finance/bank" className="text-sm font-medium text-primary hover:underline">
          Books
        </Link>
      </CardHeader>
      <CardContent>
        <ul className="divide-y divide-border">
          {cash.accounts.map((a) => (
            <li key={a.id} className="flex items-center justify-between gap-3 py-2.5 text-sm">
              <span className="flex items-center gap-2">
                {a.name}
                <Badge>{a.type}</Badge>
              </span>
              <span className={cn('tabular font-medium', Number(a.balance) < 0 && 'text-danger')}>
                {formatMoney(a.balance)}
              </span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  )
}

function LowStock({ rows }: { rows: NonNullable<Dashboard['low_stock']> }) {
  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-4">
        <div>
          <CardTitle>Running low</CardTitle>
          <CardDescription>At or below minimum stock.</CardDescription>
        </div>
        <Link to={'/reports/low_stock' as never} className="text-sm font-medium text-primary hover:underline">
          All
        </Link>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-8 text-sm text-muted-foreground">
            <PackageX className="size-5" /> Everything is above its minimum.
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {rows.map((r) => (
              <li key={r.id} className="flex items-center justify-between gap-3 py-2.5 text-sm">
                <Link to={`/inventory?item=${r.id}` as never} className="min-w-0 truncate hover:underline">
                  {r.name} <span className="text-xs text-muted-foreground">{r.code}</span>
                </Link>
                <span className="tabular shrink-0 text-right">
                  <span
                    className={cn(Number(r.quantity) <= 0 ? 'text-danger' : 'text-warning', 'font-medium')}
                  >
                    {Number(r.quantity)}
                  </span>
                  <span className="text-muted-foreground"> / {Number(r.min_stock)}</span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}

function Pending({ pending }: { pending: Dashboard['pending'] | undefined }) {
  if (!pending) return null
  const items: { count: number | null; label: string; to: string; icon: LucideIcon }[] = [
    {
      count: pending.discount_approvals,
      label: 'discount approvals waiting',
      to: '/sales/approvals',
      icon: Percent,
    },
    {
      count: pending.partner_orders,
      label: 'partner orders to review',
      to: '/sales/orders',
      icon: ClipboardList,
    },
    {
      count: pending.partner_remittances,
      label: 'invoices awaiting partner remittance',
      to: '/partners',
      icon: HandCoins,
    },
    {
      count: pending.stock_adjustments,
      label: 'stock adjustments to approve',
      to: '/inventory/operations',
      icon: AlertTriangle,
    },
  ]
  const open = items.filter((i) => (i.count ?? 0) > 0)
  if (open.length === 0) return null

  return (
    <div className="flex flex-wrap gap-2">
      {open.map(({ count, label, to, icon: Icon }) => (
        <Link
          key={label}
          to={to as never}
          className="inline-flex items-center gap-2 rounded-full border border-warning/30 bg-warning-soft px-3 py-1.5 text-sm text-warning transition-colors hover:border-warning/60"
        >
          <Icon className="size-4" />
          <span className="font-semibold">{count}</span> {label}
          <ArrowRight className="size-3.5" />
        </Link>
      ))}
    </div>
  )
}

function ShiftCard({ shift }: { shift: NonNullable<Dashboard['shift']> }) {
  return (
    <Card className="p-5">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="grid size-10 place-items-center rounded-xl bg-accent text-accent-foreground">
            <Timer className="size-5" />
          </span>
          <div>
            <p className="text-sm font-semibold">Your shift on {shift.register ?? 'the counter'}</p>
            <p className="text-xs text-muted-foreground">Open since {formatDateTime(shift.opened_at)}</p>
          </div>
        </div>
        <dl className="grid grid-cols-3 gap-6 text-sm">
          <div>
            <dt className="text-xs text-muted-foreground">Bills</dt>
            <dd className="tabular font-semibold">{shift.sales_count}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Sales</dt>
            <dd className="tabular font-semibold">{formatMoney(shift.gross)}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Cash in drawer</dt>
            <dd className="tabular font-semibold">{formatMoney(shift.expected_cash)}</dd>
          </div>
        </dl>
        <Button asChild size="sm">
          <Link to="/pos">
            <Store /> Back to POS
          </Link>
        </Button>
      </div>
    </Card>
  )
}
